import PlayIcon from "@heroicons/react/solid/PlayIcon";
import { Button } from "@shopify/polaris";
import cx from "classnames";
import React, { useMemo, useState } from "react";
import { Box } from "@novel/shared/components/Box";
import { CappedDimensionMedia } from "@novel/shared/components/CappedDimensionMedia";
import { Countdown } from "@novel/shared/components/Countdown";
import { useIsMobile } from "@novel/shared/hooks/useIsMobile";
import { ICryptoAddress } from "@novel/shared/interfaces/CryptoAddress";
import { ITokenWithMetadataAndCollection } from "@novel/shared/interfaces/Token";
import { ITokenCollectionClaimWithTokenCollection } from "@novel/shared/interfaces/TokenCollectionClaim";
import { ITokenMetadata } from "@novel/shared/interfaces/TokenMetadata";
import { IPFS_GATEWAY_PREFIX } from "@novel/shared/utils/appConstants";
import { ConditionalTooltip } from "@novel/shared/components/ConditionalTooltip";
import EyeIcon from "@heroicons/react/outline/EyeIcon";
import EyeOffIcon from "@heroicons/react/outline/EyeOffIcon";
import { useAsyncCallback } from "@novel/shared/utils/useAsyncCallback";
import { typedFrontendGalleryApi } from "@customer-gallery/utils/typedFrontendGalleryApi";
import { useNovelCustomerDispatch } from "@customer-gallery/redux/reduxHooks";
import { BigIntStr } from "@novel/shared/interfaces/shared/BigIntStr";
import { ICryptoAddressToTokenWithToken } from "@novel/shared/interfaces/CryptoAddressToToken";
interface UnclaimedGalleryItemProps {
  name?: string;
  image: string;
  video?: string;
  hasConnectedAddress?: boolean;
  quantity?: number;
  cryptoAddress?: void;
  unclaimedToken: ITokenCollectionClaimWithTokenCollection;
  onClick?: (unclaimedLineItem: ITokenCollectionClaimWithTokenCollection) => void;
  itemSelect?: boolean;
  onImageSelect?: (image: string, token: ITokenWithMetadataAndCollection) => void;
  skeleton?: boolean;
  disableClaimButtonWhileClaiming?: boolean;
}
interface ClaimedGalleryItemProps {
  name?: string;
  // selectNFTs?: (claimedToken: ITokenWithMetadataAndCollection) => void;
  image: string;
  video?: string;
  hasConnectedAddress?: boolean;
  cryptoAddressToToken?: ICryptoAddressToTokenWithToken;
  quantity?: BigIntStr;
  cryptoAddress: ICryptoAddress;
  claimedToken: ITokenWithMetadataAndCollection;
  settingState?: boolean | undefined;
  isUnrevealed?: boolean;
  onClick?: (claimedToken: ITokenMetadata | ITokenWithMetadataAndCollection) => void;
  itemSelect?: boolean;
  onImageSelect?: (image: string, token: ITokenWithMetadataAndCollection) => void;
  skeleton?: boolean;
}
export function GalleryItemCmp(props: UnclaimedGalleryItemProps | ClaimedGalleryItemProps): JSX.Element {
  // conditional dependencies make this very confusing
  const boundOnClick = useMemo(() => props.onClick && (props.cryptoAddress ? (props as any).onClick.bind(null, (props as any).claimedToken) : (props as any).onClick.bind(null, (props as any).unclaimedToken)), [props]);
  const dispatch = useNovelCustomerDispatch();
  const [hidingLoad, setHidingLoad] = useState(false);
  const isUnrevealed = !!(props as ClaimedGalleryItemProps).isUnrevealed;
  const isMobile = useIsMobile();
  const disableClaimButtonWhileClaiming = !!(props as any).disableClaimButtonWhileClaiming;
  let video;
  if (props.video) {
    if (props.video.startsWith("ipfs://")) {
      video = `${IPFS_GATEWAY_PREFIX}${props.video.replace("ipfs://", "")}`;
    } else {
      video = props.video;
    }
  }
  const toggleNftHidden = useAsyncCallback(async () => {
    setHidingLoad(true);
    await typedFrontendGalleryApi.postReq("/api/customer/hide-nft", {
      reqBody: {
        contractAddress: (props as ClaimedGalleryItemProps)?.claimedToken.collectionSmartContract?.address,
        tokenId: (props as ClaimedGalleryItemProps)?.claimedToken?.tokenId,
        isHidden: !(props as ClaimedGalleryItemProps)?.cryptoAddressToToken?.isHidden,
        address: (props as ClaimedGalleryItemProps)?.cryptoAddress?.address
      }
    });
    dispatch({
      type: "TOGGLE_TOKEN_HIDDEN",
      payload: {
        id: (props as ClaimedGalleryItemProps)?.cryptoAddressToToken?.id,
        status: !(props as ClaimedGalleryItemProps)?.cryptoAddressToToken?.isHidden
      }
    });
    setHidingLoad(false);
  }, [(props as ClaimedGalleryItemProps).claimedToken?.collectionSmartContract?.address, (props as ClaimedGalleryItemProps).claimedToken?.id, (props as ClaimedGalleryItemProps).cryptoAddressToToken?.isHidden, (props as ClaimedGalleryItemProps).claimedToken?.tokenId, dispatch]);
  return <Box column className={cx("gallery-nft-image-and-text-wrapper", {
    claimed: !!props.cryptoAddress
  })}>
            {!(props as ClaimedGalleryItemProps).skeleton && (props as ClaimedGalleryItemProps).settingState && (props as ClaimedGalleryItemProps).claimedToken && <div className="select-area-zone">
                        {/* <label className="flex items-center">
                            <input
                                type="checkbox"
                                className="form-checkbox w-8 h-8 cursor-pointer rounded-lg"
                                onChange={() =>
                                    (props as ClaimedGalleryItemProps)?.selectNFTs?.(
                                        (props as ClaimedGalleryItemProps).claimedToken,
                                    )
                                }
                            />
                         </label> */}
                        <button className="hide-icon-button" onClick={toggleNftHidden} disabled={!!hidingLoad}>
                            {hidingLoad ? <svg className="w-5 h-5 mr-3 -ml-1 text-white animate-spin" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                                </svg> : (props as ClaimedGalleryItemProps).cryptoAddressToToken?.isHidden ? <EyeIcon color="white" /> : <EyeOffIcon color="white" />}
                        </button>
                    </div>}
            {(props as ClaimedGalleryItemProps).claimedToken && (props as ClaimedGalleryItemProps).claimedToken?.collectionSmartContract?.contractType === "1155" && <div className="quantity-box">
                        <div className="quantity-inside">
                            x{(props as ClaimedGalleryItemProps)?.quantity}
                        </div>
                    </div>}

            <Box className={cx(`gallery-nft-image-wrapper ${(props as ClaimedGalleryItemProps).cryptoAddressToToken && (props as ClaimedGalleryItemProps).cryptoAddressToToken?.isHidden && (props as ClaimedGalleryItemProps).cryptoAddressToToken?.isHidden ? "wrapper-hidden" : ""} ${props.itemSelect ? "mb-3" : ""}`, {
      "is-skeleton": props.skeleton
    })} onClick={props.cryptoAddress ? props.itemSelect ? () => props.onImageSelect && props.onImageSelect(props.image, (props as any).claimedToken) : boundOnClick : undefined}>
                <Box className={cx("gallery-nft-image-container", {
        galleryUnclaimed: !props.cryptoAddress,
        "no-width": props.skeleton
      })}>
                    {props.video ? isMobile ? <div>
                                <CappedDimensionMedia className={cx("gallery-nft-image", {
            "is-unrevealed": isUnrevealed
          })} src={props.image} calculation="cover" />

                                <PlayIcon height={50} width={50} stroke="black" fill="white" className="gallery-nft-image-play-icon" />
                            </div> : <CappedDimensionMedia className={cx("gallery-nft-image", {
          "is-unrevealed": isUnrevealed
        })} src={video} type="video" poster={props.image} autoPlay loop controls={false} muted calculation="contain" /> : props.image ? <CappedDimensionMedia className={cx("gallery-nft-image", {
          "is-unrevealed": isUnrevealed
        })} src={props.image} calculation="cover" /> : <div className="no-content-background">
                            <div className="imageCircular-card rounded-full gradientBg" />
                            <span className="text-no-content">Content not available yet</span>
                        </div>}
                </Box>

                {!props.cryptoAddress && <div className="center-above-image claim-button-wrapper">
                        <ConditionalTooltip show={disableClaimButtonWhileClaiming} content="Claiming your token(s)...">
                            <ConditionalTooltip show={!disableClaimButtonWhileClaiming} content="Claiming your token will allow you to unlock any features granted by the creator as well as sell on secondary markets such as OpenSea.">
                                <Button primary onClick={boundOnClick} disabled={!props.hasConnectedAddress || disableClaimButtonWhileClaiming}>
                                    Claim
                                    {props.quantity && BigIntStr.greaterThan(props.quantity, 1) ? ` ${props.quantity}` : ""}
                                </Button>
                            </ConditionalTooltip>
                        </ConditionalTooltip>
                    </div>}
                {isUnrevealed && <div className="center-above-image countdown-wrapper">
                        <Countdown breakOnIn prependStr="Reveals" countdownTo={(props as ClaimedGalleryItemProps).claimedToken.collectionSmartContract.collectionMetadata.revealingOn!} />
                    </div>}
            </Box>
            {props.name && <Box justifyContent="center" className="gallery-nft-image-name" onClick={props.cryptoAddress ? props.itemSelect ? () => props.onImageSelect && props.onImageSelect(props.image, (props as any).claimedToken) : boundOnClick : undefined}>
                    <div className="gallery-nft-image-name-text">{props.name}</div>
                </Box>}
        </Box>;
}